import React, { useState } from "react";
import { Button, Modal, Form, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaDownload, FaEye, FaUpload } from "react-icons/fa";
import api from "../../../api";
import swal from "sweetalert";
import FilePreview from "./FilePreview";

const getFilenameFromDisposition = (disposition, fallback) => {
 if (!disposition) return fallback;

 const encodedMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
 if (encodedMatch?.[1]) {
 try {
 return decodeURIComponent(encodedMatch[1]);
 } catch (_) {
 return encodedMatch[1];
 }
 }

 const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
 return plainMatch?.[1] || fallback;
};

const ReceiptActions = ({
 id,
 type,
 receipt,
 onUploadSuccess,
 canUpload = true,
 canDownload = true,
 uploadField = "receipt",
 uploadEndpoint = "upload-receipt",
 viewEndpoint = "view-receipt",
  downloadEndpoint = "download-receipt",
  title = "Receipt",
  query = "",
}) => {
 const [showUploadModal, setShowUploadModal] = useState(false);
 const [file, setFile] = useState(null);
 const [uploading, setUploading] = useState(false);
 const [previewing, setPreviewing] = useState(false);
 const [downloading, setDownloading] = useState(false);

 const hasReceipt = Boolean(receipt?.filePath || receipt?.fileUrl || receipt?.fileName);

 const handleFileChange = (e) => {
 const selectedFile = e.target.files?.[0];
 if (!selectedFile) {
 setFile(null);
 return;
 }

 setFile(selectedFile);
 };

 const handleUpload = async () => {
 if (!file) {
 return swal("Error", `Please select a file to upload`, "error");
 }

 setUploading(true);
 const formData = new FormData();
 formData.append(uploadField, file);

 try {
    const res = await api.patch(`/${type}/${id}/${uploadEndpoint}${query}`, formData, {
 headers: { "Content-Type": "multipart/form-data" },
 });

 if (res.data.success) {
 swal("Success", `${title} uploaded successfully`, "success");
 setShowUploadModal(false);
 setFile(null);
 if (onUploadSuccess) await onUploadSuccess();
 }
 } catch (error) {
 swal("Error", error.response?.data?.message || `Failed to upload ${title.toLowerCase()}`, "error");
 } finally {
 setUploading(false);
 }
 };

 const handleDownload = async () => {
 if (!hasReceipt) return;

 setDownloading(true);
 try {
    const res = await api.get(`/${type}/${id}/${downloadEndpoint}${query}`, {
 responseType: "blob",
 timeout: 120000,
 suppressErrorToast: true,
 });

 const contentType = res.headers?.["content-type"] || "application/octet-stream";
 const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: contentType }));
 const link = document.createElement("a");
 link.href = blobUrl;
 link.download = getFilenameFromDisposition(
 res.headers?.["content-disposition"],
 `${type}-${title.toLowerCase()}.bin`
 );
 document.body.appendChild(link);
 link.click();
 document.body.removeChild(link);
 window.URL.revokeObjectURL(blobUrl);
 } catch (error) {
 swal("Error", error.response?.data?.message || `Failed to download ${title.toLowerCase()}`, "error");
 } finally {
 setDownloading(false);
 }
 };

 const handlePreview = async () => {
 if (!hasReceipt) return;

 setPreviewing(true);
 const previewWindow = window.open("", "_blank");
 try {
    const res = await api.get(`/${type}/${id}/${viewEndpoint}${query}`, {
 responseType: "blob",
 timeout: 120000,
 suppressErrorToast: true,
 });

 const contentType = res.headers?.["content-type"] || "application/octet-stream";
 const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: contentType }));

 if (previewWindow) {
 previewWindow.location.href = blobUrl;
 } else {
 window.open(blobUrl, "_blank");
 }
 } catch (error) {
 if (previewWindow) previewWindow.close();
 swal("Error", error.response?.data?.message || `Failed to preview ${title.toLowerCase()}`, "error");
 } finally {
 setPreviewing(false);
 }
 };

 const tooltip = (text) => <Tooltip id={`receipt-${id}`}>{text}</Tooltip>;

 return (
 <>
 <div className="d-flex align-items-center gap-1">
 {canDownload && (
 <OverlayTrigger
 placement="top"
 overlay={tooltip(hasReceipt ? `Preview ${title.toLowerCase()}` : `${title} not available`)}
 >
 <span>
 <Button
 variant="outline-info"
 size="sm"
 className="p-1 px-2 shadow-none"
 disabled={!hasReceipt || previewing}
 onClick={handlePreview}
 >
 {previewing ? <Spinner animation="border" size="sm" /> : <FaEye />}
 </Button>
 </span>
 </OverlayTrigger>
 )}

 {canDownload && (
 <OverlayTrigger
 placement="top"
 overlay={tooltip(hasReceipt ? `Download ${title.toLowerCase()}` : `${title} not available`)}
 >
 <span>
 <Button
 variant="outline-success"
 size="sm"
 className="p-1 px-2 shadow-none"
 disabled={!hasReceipt || downloading}
 onClick={handleDownload}
 >
 {downloading ? <Spinner animation="border" size="sm" /> : <FaDownload />}
 </Button>
 </span>
 </OverlayTrigger>
 )}

 {canUpload && (
 <OverlayTrigger
 placement="top"
 overlay={tooltip(hasReceipt ? `Replace ${title.toLowerCase()}` : `Upload ${title.toLowerCase()}`)}
 >
 <Button
 variant="outline-primary"
 size="sm"
 className="p-1 px-2 shadow-none"
 onClick={() => setShowUploadModal(true)}
 >
 <FaUpload />
 </Button>
 </OverlayTrigger>
 )}
 </div>

 <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)} centered scrollable>
 <Modal.Header closeButton>
 <Modal.Title className="fs-5">{title}</Modal.Title>
 </Modal.Header>
 <Modal.Body className="p-3">
 <div className="small text-muted mb-2">
 Upload the payment receipt for this bill. It can be previewed and downloaded later from the same row.
 </div>

 {receipt && (receipt.filePath || receipt.fileUrl || receipt.fileName) && (
 <div className="mb-3">
 <label className="form-label small fw-bold">Existing Receipt:</label>
 <FilePreview file={receipt} />
 </div>
 )}

 <Form.Group>
 <Form.Label className="small fw-bold">
 {hasReceipt ? "Replace with New File:" : "Select File:"}
 </Form.Label>
 <Form.Control
 type="file"
 accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,image/*"
 onChange={handleFileChange}
 size="sm"
 />
 </Form.Group>

 {file && (
 <div className="mt-3">
 <label className="form-label small fw-bold text-primary">New Selection:</label>
 <FilePreview file={file} onRemove={() => setFile(null)} />
 </div>
 )}
 </Modal.Body>
 <Modal.Footer className="p-2">
 <Button variant="light" size="sm" onClick={() => setShowUploadModal(false)}>
 Cancel
 </Button>
 <Button variant="primary" size="sm" onClick={handleUpload} disabled={uploading || !file}>
 {uploading ? (
 <>
 <Spinner animation="border" size="sm" className="me-1" />
 Uploading
 </>
 ) : hasReceipt ? (
 "Replace"
 ) : (
 "Upload"
 )}
 </Button>
 </Modal.Footer>
 </Modal>
 </>
 );
};

export default ReceiptActions;
